import { z } from "zod";
import { CalendarDate, EntityId, Priority, TaskStatus } from "./entities";

export const DueFilter = z.discriminatedUnion("type", [
  /** Due today. */
  z.object({ type: z.literal("today") }),
  /** Due before today and not completed. */
  z.object({ type: z.literal("overdue") }),
  /** Due today or within the next `days` days. */
  z.object({ type: z.literal("upcoming"), days: z.number().int().positive() }),
  z.object({ type: z.literal("range"), from: CalendarDate, to: CalendarDate }),
  /** No due date. */
  z.object({ type: z.literal("none") }),
]);
export type DueFilter = z.infer<typeof DueFilter>;

export const ScheduledFilter = z.discriminatedUnion("type", [
  z.object({ type: z.literal("today") }),
  z.object({ type: z.literal("range"), from: CalendarDate, to: CalendarDate }),
  z.object({ type: z.literal("none") }),
]);
export type ScheduledFilter = z.infer<typeof ScheduledFilter>;

export const TaskFilters = z.object({
  /** Built-in attention rules, evaluated in core. */
  attention: z.enum(["inbox", "anytime", "upcoming"]).optional(),
  sourceNoteId: EntityId.optional(),
  /** `null` inside the array means no project. */
  projectIds: z.array(EntityId.nullable()).optional(),
  status: z.array(TaskStatus).optional(),
  due: DueFilter.optional(),
  scheduled: ScheduledFilter.optional(),
  priority: z.array(Priority).optional(),
  /** Completed within the last N days (for "completed this week"). */
  completedWithinDays: z.number().int().positive().optional(),
  evening: z.boolean().optional(),
  /** Any of these tags (case-insensitive). */
  tags: z.array(z.string()).optional(),
  /** Free text; matched against title and description case-insensitively. */
  text: z.string().optional(),
});
export type TaskFilters = z.infer<typeof TaskFilters>;

export const TaskSortKey = z.enum([
  "manual",
  "dueDate",
  "scheduledAt",
  "priority",
  "createdAt",
  "updatedAt",
  "completedAt",
  "title",
]);
export type TaskSortKey = z.infer<typeof TaskSortKey>;

export const TaskSort = z.object({
  by: TaskSortKey,
  direction: z.enum(["asc", "desc"]),
});
export type TaskSort = z.infer<typeof TaskSort>;

export const TaskGroupBy = z.enum([
  "none",
  "project",
  "dueDate",
  "priority",
  "heading",
  "status",
  "plannedDate",
]);
export type TaskGroupBy = z.infer<typeof TaskGroupBy>;

export const TaskDisplay = z.object({
  density: z.enum(["compact", "comfortable"]).default("comfortable"),
  showCompleted: z.boolean().default(false),
  showProject: z.boolean().default(true),
});
export type TaskDisplay = z.infer<typeof TaskDisplay>;

export const TaskView = z.object({
  id: z.string(),
  name: z.string(),
  filters: TaskFilters,
  sort: TaskSort,
  groupBy: TaskGroupBy.default("none"),
  display: TaskDisplay.default({ density: "comfortable", showCompleted: false, showProject: true }),
});
export type TaskView = z.infer<typeof TaskView>;
export type TaskViewInput = z.input<typeof TaskView>;
